// Based on https://github.com/coderiety/formatted-text (MIT, see LICENSE.txt),
// converted to TypeScript.

import { Fragment } from 'react';
import type { ReactNode } from 'react';

export type Link = {
  // Where the link starts within its line.
  index: number;
  // The link as it appears in the text.
  text: string;
  url: string;
};

export type LinkRenderer = (link: Link) => ReactNode;

type Props = {
  children?: string | null;
  className?: string;
  itemProp?: string;
  linkRenderer?: LinkRenderer;
};

type RenderOptions = { linkRenderer: LinkRenderer };

const PARAGRAPH_SPLIT = /\n{2,}/;

const LINE_SPLIT = /\n/;

const LINK = /(?:\S+)(\w+:\/\/)\S*|([^\s.]+)(\.[a-z-]{2,63})+\S*/gi;

const DEFAULT_PROTOCOL = 'http://';

const HTTP_URL = /^https?:\/\//i;

const TERMINATORS = '.,;:?!';

const WRAPPERS: Record<string, string> = {
  '(': ')',
  '[': ']',
  '"': '"',
  "'": "'",
  '<': '>'
};

const unwrap = (text: string, index: number): [string, number] => {
  const [first, last] = [text[0], text[text.length - 1]];
  if (TERMINATORS.indexOf(last) > -1) return unwrap(text.slice(0, -1), index);
  if (WRAPPERS[first] === last) return unwrap(text.slice(1, -1), index + 1);
  return [text, index];
};

const toUrl = (link: string, protocol: string | undefined, preTld: string) => {
  if (protocol) return link;
  if (preTld.includes('@')) return `mailto:${link}`;
  return DEFAULT_PROTOCOL + link;
};

const getLinks = (text: string): Link[] => {
  const links: Link[] = [];
  for (const match of text.matchAll(LINK)) {
    const [matched, protocol, preTld, tld] = match;

    // To qualify as a link, either the protocol or TLD must be specified.
    if (!protocol && !tld) continue;

    const [link, index] = unwrap(matched, match.index);

    // Any scheme but http and https stays text: a `javascript:` URL runs
    // script when clicked. The test is on the whole link, as `protocol`
    // captures only the last characters of the scheme.
    if (protocol && !HTTP_URL.test(link)) continue;

    links.push({ index, text: link, url: toUrl(link, protocol, preTld) });
  }
  return links;
};

const renderLinks = (
  text: string,
  key: number,
  options: RenderOptions
): ReactNode => {
  const links = getLinks(text);
  if (!links.length) return text;
  const { length } = links;
  return links.reduce<{ index: number; components: ReactNode[] }>(
    (parts, link, i) => {
      const from = link.index;
      const to = from + link.text.length;
      return {
        index: to,
        components: parts.components.concat(
          text.slice(parts.index, from),
          <Fragment key={`${key}-${i}`}>{options.linkRenderer(link)}</Fragment>,
          i === length - 1 ? text.slice(to) : null
        )
      };
    },
    { index: 0, components: [] }
  ).components;
};

const renderParagraph = (text: string, options: RenderOptions) => {
  const lines = text.trim().split(LINE_SPLIT);
  return lines.reduce<ReactNode[]>(
    (paragraph, line, i) =>
      paragraph.concat(
        renderLinks(line, i, options),
        i === lines.length - 1 ? null : <br key={i} />
      ),
    []
  );
};

// Not `children ?? ''`: callers in Flow and plain JavaScript aren't held to
// the type, and anything but a string renders as nothing.
const renderParagraphs = ({
  children,
  ...options
}: RenderOptions & { children: string | null }) => {
  const text = typeof children === 'string' ? children : '';
  const paragraphs = text.trim().split(PARAGRAPH_SPLIT);
  return paragraphs.map((paragraph, i) =>
    paragraph ? <p key={i}>{renderParagraph(paragraph, options)}</p> : null
  );
};

// Hoisted rather than written inline as a parameter default so that omitting
// the prop yields the same function across renders.
const defaultLinkRenderer: LinkRenderer = ({ url, text }) => (
  <a href={url}>{text}</a>
);

const FormattedText = ({
  children = '',
  className,
  itemProp,
  linkRenderer = defaultLinkRenderer
}: Props) => (
  <div className={className} itemProp={itemProp}>
    {renderParagraphs({ children, linkRenderer })}
  </div>
);

export function noRefLinkRenderer({ url, text }: Link) {
  return (
    <a rel="noreferrer nofollow noopener" href={url}>
      {text}
    </a>
  );
}

export function noLinkRenderer({ text }: Link) {
  return <span>{text}</span>;
}

export default FormattedText;
